import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import AnimatedBackground from '../../utils/AnimatedBackground';
import Footer from '../../utils/Footer';
import TopBar from '../../utils/TopBar';
import { useCashflowDash } from '../hooks/Cashflow';
import { CashflowTrendData, ChartDataPoint } from '../models/ModelCashflow';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

function CashflowTrendChart({ data }: { data: CashflowTrendData }) {
  if (!data?.months || !data?.base_cashflow) return null;

  // Sort the chart data from oldest to newest
  const chartData = data.months.map((month: string, index: number) => ({
    name: month,
    'Base Cashflow': data.base_cashflow[index]
  })).sort((a: ChartDataPoint, b: ChartDataPoint) => new Date(a.name).getTime() - new Date(b.name).getTime());

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart 
        data={chartData} 
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
      >
        <defs>
          <linearGradient id="gradient-cashflow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <XAxis 
          dataKey="name" 
          stroke="#888888"
          tickFormatter={(value) => {
            try {
              return new Date(value).toLocaleDateString('default', { 
                month: 'short',
                year: '2-digit'
              });
            } catch (e) {
              return value;
            }
          }}
          axisLine={{ strokeWidth: 1, stroke: '#374151' }}
          tickLine={{ stroke: '#374151' }}
        />
        <YAxis 
          stroke="#888888"
          tickFormatter={(value) => formatCurrency(value)}
          axisLine={{ strokeWidth: 1, stroke: '#374151' }}
          tickLine={{ stroke: '#374151' }}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
          labelStyle={{ color: '#9ca3af' }}
          formatter={(value: number) => [formatCurrency(value), 'Base Cashflow']}
          labelFormatter={(label) => {
            try {
              return new Date(label).toLocaleDateString('default', { 
                month: 'long',
                year: 'numeric'
              });
            } catch (e) {
              return label;
            }
          }}
        />
        <Legend 
          verticalAlign="top" 
          height={36}
          formatter={(value) => <span style={{ color: '#9ca3af' }}>{value}</span>}
        />
        <Line
          type="monotone"
          dataKey="Base Cashflow"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={{ stroke: '#3b82f6', strokeWidth: 2, r: 4, fill: '#1f2937' }}
          activeDot={{ stroke: '#3b82f6', strokeWidth: 2, r: 6, fill: '#1f2937' }}
          fill="url(#gradient-cashflow)"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

const Cashflow: React.FC = () => {
  const {
    cashflowHistory,
    trendData,
    fetchCashflowData,
    resetCashflowData,
    isLoading,
    error,
  } = useCashflowDash();

  useEffect(() => {
    fetchCashflowData();
  }, [fetchCashflowData]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex items-center gap-2 text-blue-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading cashflow data...</span>
        </div>
      </div>
    );
  }

  if (error === 'income') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">No Income Data Available</h2>
          <p className="text-gray-400 mb-6">Please add some income data to view your cashflow.</p>
          <Link to="/income">
            <Button className="bg-green-600 hover:bg-green-500 text-white">
              Add Income Data
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (error === 'expense') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">No Expense Data Available</h2>
          <p className="text-gray-400 mb-6">Please add some expense data to view your cashflow.</p>
          <Link to="/expenses">
            <Button className="bg-red-600 hover:bg-red-500 text-white">
              Add Expense Data
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!cashflowHistory || cashflowHistory.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">No Cashflow Data Available</h2>
          <p className="text-gray-400 mb-6">Please add both income and expense data to view your cashflow.</p>
          <div className="flex gap-4 justify-center">
            <Link to="/income">
              <Button className="bg-green-600 hover:bg-green-500 text-white">
                Add Income
              </Button>
            </Link>
            <Link to="/expenses">
              <Button className="bg-red-600 hover:bg-red-500 text-white">
                Add Expenses
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar 
          title="Cashflow Dashboard"
          subtitle="Track and analyze your cashflow"
          isLoading={isLoading}
        />
        
        <main className="flex-grow container mx-auto py-2 px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="md:col-span-2 h-full"
            >
              <Card className="bg-black border-gray-800 h-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Monthly Cashflow</CardTitle>
                    <CardDescription className="text-gray-300">
                      Historical cashflow data
                    </CardDescription>
                  </div>
                  <div className="flex gap-4">
                    <Button
                      onClick={resetCashflowData}
                      className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-2 rounded-full transition duration-300 ease-in-out transform hover:scale-105"
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Recalculate
                    </Button>
                    <Link to="/cashflow/edit">
                      <Button className="bg-yellow-600 hover:bg-yellow-500 text-white font-semibold px-6 py-2 rounded-full transition duration-300 ease-in-out transform hover:scale-105">
                        Edit Cashflow
                      </Button>
                    </Link>
                    <Link to="/income">
                      <Button className="bg-green-600 hover:bg-green-500 text-white font-semibold px-6 py-2 rounded-full transition duration-300 ease-in-out transform hover:scale-105">
                        Manage Income
                      </Button>
                    </Link>
                    <Link to="/expenses">
                      <Button className="bg-red-600 hover:bg-red-500 text-white font-semibold px-6 py-2 rounded-full transition duration-300 ease-in-out transform hover:scale-105">
                        Manage Expenses
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className="text-left text-gray-300 py-3 px-4"></th>
                          {cashflowHistory.map((item) => (
                            <th key={item.month} className="text-right text-gray-300 py-3 px-4">
                              {new Date(item.month).toLocaleDateString('default', { month: 'short', year: '2-digit', timeZone: 'UTC' })}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Income Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+) Income</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-green-400">
                              {formatCurrency(item.income)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(-) Expenses</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-red-400">
                              {formatCurrency(item.expenses)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">(=) Base Cashflow</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className={`py-3 px-4 text-right ${item.base_cashflow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(item.base_cashflow)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={cashflowHistory.length + 1} className="py-2"></td>
                        </tr>

                        {/* VR Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial VR</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.initial_vr)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+) Gains</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-green-400">
                              {formatCurrency(item.gains_vr)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(-) Expenses</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-red-400">
                              {formatCurrency(item.expenses_vr)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">(=) Final VR</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.initial_vr + item.gains_vr - item.expenses_vr)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={cashflowHistory.length + 1} className="py-2"></td>
                        </tr>

                        {/* Investments Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial Investments</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.made_investments)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+) Investments</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className={`py-3 px-4 text-right ${item.projected_investments >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(item.projected_investments)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(-) Investments</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-red-400">
                              {formatCurrency(item.projected_investments < 0 ? Math.abs(item.projected_investments) : 0)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-4 text-gray-300">Final Investments</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_investments)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={cashflowHistory.length + 1} className="py-2"></td>
                        </tr>

                        {/* Cash Reserve Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial Cash Reserve</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.initial_cash_reserve)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+) Cash Reserve</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className={`py-3 px-4 text-right ${item.projected_cash_reserve >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(item.projected_cash_reserve)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(=) Final Cash Reserve</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_cash_reserve)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={cashflowHistory.length + 1} className="py-2"></td>
                        </tr>

                        {/* Final Cash Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial Cash</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.initial_cash)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Base Cashflow</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.base_cashflow)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Net Investments</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className={`py-3 px-4 text-right ${-item.projected_investments >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(-item.projected_investments)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Net Cash Reserve</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className={`py-3 px-4 text-right ${-item.projected_cash_reserve >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(-item.projected_cash_reserve)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">Final Cash</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_cash)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={cashflowHistory.length + 1} className="py-2"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="md:col-span-2"
            >
              <Card className="bg-black border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white">Cash Savings</CardTitle>
                  <CardDescription className="text-gray-300">
                    Current month summary
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className="text-left text-gray-300 py-3 px-4"></th>
                          {cashflowHistory.map((item) => (
                            <th key={item.month} className="text-right text-gray-300 py-3 px-4">
                              {new Date(item.month).toLocaleDateString('default', { month: 'short', year: '2-digit', timeZone: 'UTC' })}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Investments</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_investments)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Reserve</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_cash_reserve)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Available</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_cash)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 border-b font-semibold">
                          <td className="py-3 px-5 text-gray-300">Cash Savings</td>
                          {cashflowHistory.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(
                                item.final_investments +
                                item.final_cash_reserve +
                                item.final_cash
                              )}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="md:col-span-2"
            >
              <Card className="bg-black border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white">Cashflow Trend</CardTitle>
                  <CardDescription className="text-gray-300">
                    Net cashflow trend over time - last 12 months
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {trendData && <CashflowTrendChart data={trendData} />}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default Cashflow;
